import { useEffect, useState } from "react";
import {
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  useMediaQuery,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { AudioIcons, MenuBorderColor } from "shared/constants";
import TTSDownloadNotification from "./TTSDownloadNotification";
import SampleRateSelector from "./SampleRateSelector";
import AudioWaveForm from "./AudioWaveForm";
import { useAtomValue, useSetAtom } from "jotai";
import { selectedValuesAtom } from "../../../atoms";
import QuotaPercent from "../components/QuotaPercent";
import { quotaUsageAtom } from "atoms/quota";

interface TTSAudioPlayerProps {
  onPlayClick: () => void;
  shouldDisableButtons: boolean;
  isStopDisabled: boolean;
  onStopClick: () => void;
  showError: boolean;
  analyser?: AnalyserNode | null;
}

function TTSAudioPlayer({
  onPlayClick,
  shouldDisableButtons,
  isStopDisabled,
  onStopClick,
  showError,
  analyser,
}: TTSAudioPlayerProps): JSX.Element {
  const iconStyle = {
    width: "35px",
    height: "35px",
  };

  const [showDownloadNotification, setShowDownloadNotification] =
    useState<boolean>(false);
  const [showPlayButton, setShowPlayButton] = useState<boolean>(true);
  const setSelectedValues = useSetAtom(selectedValuesAtom);
  const isMobileScreen = useMediaQuery("(max-width:600px)");
  const quota = useAtomValue(quotaUsageAtom);


  useEffect(() => {
    // if the stop button is disabled or an error has occured, then the play button should be shown again
    if (isStopDisabled || showError) {
      setShowPlayButton(true);
    }
  }, [isStopDisabled, showError]);

  const handlePlayPauseClick = () => {
    setShowPlayButton(!showPlayButton);
    onPlayClick();
  };

  const playPauseStyle = {
    ...iconStyle,
    color: shouldDisableButtons ? AudioIcons.Disabled : AudioIcons.Enabled,
  };

  const handleSampleRateChange = (sampleRate: string) => {
    setSelectedValues((prev) => ({
      ...prev,
      selectedSampleRate: sampleRate,
    }));
  };

  return (
    <>
      <Grid
        id="audio-player-options"
        sx={{
          padding: "8px 12px 8px 12px",
          border: `1px solid ${MenuBorderColor}`,
          borderTop: 0,
        }}
        container
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" spacing={1}>
          <div style={{
            margin: "auto"
          }}>
            <QuotaPercent
              total={quota?.totalQuota ?? 0}
              used={(quota?.totalQuota ?? 0) - (quota?.remainingQuota ?? 0)}
            />
          </div>
          <IconButton
            onClick={
              !showPlayButton && isStopDisabled
                ? () => null
                : handlePlayPauseClick
            }
            sx={{ padding: "4px" }}
            disabled={shouldDisableButtons}
            data-testid="play-pause-button"
          >
            {}
            {showPlayButton ? (
              <PlayCircleOutlineIcon
                sx={playPauseStyle}
                data-testid="play-icon"
              />
            ) : isStopDisabled ? (
              <CircularProgress
                size="28px"
                sx={{ color: AudioIcons.Enabled }}
                data-testid="loading-icon"
              />
            ) : (
              <PauseCircleOutlineIcon
                sx={playPauseStyle}
                data-testid="pause-icon"
              />
            )}
          </IconButton>
          <IconButton
            onClick={onStopClick}
            sx={{ padding: "4px" }}
            disabled={isStopDisabled}
            data-testid="stop-button"
          >
            <StopCircleOutlinedIcon
              sx={{
                ...iconStyle,
                color: isStopDisabled
                  ? AudioIcons.Disabled
                  : AudioIcons.Enabled,
              }}
              data-testid="stop-icon"
            />
          </IconButton>
        </Stack>
        <Grid item xs sx={{ mx: 2, height: "100%" }}>
          <AudioWaveForm analyser={analyser ?? null} />
        </Grid>
        {!isMobileScreen && (
            <SampleRateSelector onSampleRateChange={handleSampleRateChange} />
          )}
        {!isMobileScreen && (
          <Grid item>
            <IconButton
              onClick={() => setShowDownloadNotification(true)}
              disabled={shouldDisableButtons}
              data-testid="download-button"
            >
              <FileDownloadOutlinedIcon
                sx={{
                  width: "32px",
                  height: "32px",
                  color: shouldDisableButtons
                    ? AudioIcons.Disabled
                    : AudioIcons.Active,
                }}
                data-testid="download-icon"
              />
            </IconButton>
          </Grid>
        )}
      </Grid>
      {showDownloadNotification && (
        <TTSDownloadNotification
          open={showDownloadNotification}
          closeNotification={() => setShowDownloadNotification(false)}
        />
      )}
    </>
  );
}

export default TTSAudioPlayer;
